import { format, isValid, parse, parseISO } from "date-fns"
import * as React from "react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { DateField } from "./date-field"

interface DateTimeFieldProps {
  id?: string
  value?: string
  onValueChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  onBlur?: () => void
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  dateAriaLabel?: string
  timeAriaLabel?: string
  className?: string
  buttonClassName?: string
  minuteStep?: number
}

const localDateTimeFormats = [
  "yyyy-MM-dd'T'HH:mm:ss.SSS",
  "yyyy-MM-dd'T'HH:mm:ss.SS",
  "yyyy-MM-dd'T'HH:mm:ss.S",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
] as const

export function DateTimeField({
  id,
  value,
  onValueChange,
  placeholder = "Select date and time",
  disabled = false,
  onBlur,
  ariaInvalid,
  ariaDescribedBy,
  dateAriaLabel,
  timeAriaLabel,
  className,
  buttonClassName,
  minuteStep = 5,
}: DateTimeFieldProps) {
  const parsedValue = parseDateTimeLike(value)
  const [draftDate, setDraftDate] = React.useState<string | undefined>(
    parsedValue ? format(parsedValue, "yyyy-MM-dd") : undefined,
  )
  const [draftTime, setDraftTime] = React.useState<string>(
    parsedValue ? format(parsedValue, "HH:mm") : "00:00",
  )

  React.useEffect(() => {
    setDraftDate(parsedValue ? format(parsedValue, "yyyy-MM-dd") : undefined)
    setDraftTime(parsedValue ? format(parsedValue, "HH:mm") : "00:00")
  }, [parsedValue])

  const emitDateTime = React.useCallback(
    (nextDate: string | undefined, nextTime: string) => {
      if (!nextDate) {
        onValueChange(undefined)
        return
      }

      const nextValue = `${nextDate}T${nextTime}`
      onValueChange(nextValue)
    },
    [onValueChange],
  )

  return (
    <div
      className={cn(
        "grid gap-2 md:grid-cols-[minmax(0,1fr)_9rem_auto]",
        className,
      )}
    >
      <DateField
        id={id}
        value={draftDate}
        onValueChange={(nextDate) => {
          setDraftDate(nextDate)
          emitDateTime(nextDate, draftTime)
        }}
        placeholder={placeholder}
        disabled={disabled}
        onBlur={onBlur}
        ariaInvalid={ariaInvalid}
        ariaDescribedBy={ariaDescribedBy}
        ariaLabel={dateAriaLabel}
        buttonClassName={buttonClassName}
      />

      <Input
        id={id ? `${id}-time` : undefined}
        type="time"
        step={Math.max(1, minuteStep) * 60}
        value={draftTime}
        disabled={disabled}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={timeAriaLabel}
        onChange={(event) => {
          const nextTime = event.target.value
          const parsedTime = parse(nextTime, "HH:mm", new Date())
          if (
            !isValid(parsedTime) ||
            format(parsedTime, "HH:mm") !== nextTime
          ) {
            return
          }

          setDraftTime(nextTime)
          emitDateTime(draftDate, nextTime)
        }}
        className="h-11"
      />
    </div>
  )
}

function parseDateTimeLike(value?: string) {
  if (!value || !value.trim()) {
    return undefined
  }

  const trimmed = value.trim()
  for (const dateFormat of localDateTimeFormats) {
    const parsed = parse(trimmed, dateFormat, new Date())
    if (!isValid(parsed)) {
      continue
    }

    if (format(parsed, dateFormat) !== trimmed) {
      continue
    }

    return parsed
  }

  const parsedIso = parseISO(trimmed)
  if (isValid(parsedIso)) {
    return parsedIso
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}
